import { Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Router } from '@angular/router';

import { createSelector, Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, map } from 'rxjs';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { AccountSelectors } from '@core/account-data';
import { FeatureGuardFacade, GuardedFeature } from '@core/feature-guard-legacy';
import { RequisitionsConfigSelectors } from '@recruitee/requisitions-settings-data';

const canAccessRequisitions = createSelector(
  AccountSelectors.areRequisitionsEnabled,
  RequisitionsConfigSelectors.isLoaded,
  RequisitionsConfigSelectors.requisitionsFeatureEnabled,
  AbilitiesSelectors.hasOne(Ability.ViewRequisitions),
  (enabledInAccount, requisitionsConfigLoaded, enabledInConfig, hasAbility) => {
    const isEnabled = requisitionsConfigLoaded ? enabledInConfig : enabledInAccount;
    return isEnabled && hasAbility;
  },
);

export const selectCanAccessRequisitions: (store: Store) => Observable<boolean> = (store: Store) =>
  store.select(canAccessRequisitions);

@Injectable({
  providedIn: 'root',
})
export class RequisitionsModuleGuard {
  constructor(
    private store: Store,
    private featureGuard: FeatureGuardFacade,
    private router: Router,
  ) {}

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return combineLatest([
      this.featureGuard.checkFeatureAvailability(GuardedFeature.Requisitions),
      selectCanAccessRequisitions(this.store),
    ]).pipe(
      map(
        ([hasFeature, canAccess]) =>
          (hasFeature && canAccess) || this.router.createUrlTree(['/dashboard']),
      ),
    );
  }
}
